import { IconButton } from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import clsx from 'clsx';
import _ from 'lodash';
import React, {
  FC,
  Fragment,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStyles } from './styles';

const DEFAULT_OFFSET = 150;
const DEFAULT_SCROLL_TO = 0;

export const isWindow = (item: HTMLElement | Window): item is Window =>
  'scrollY' in (item as Window);

enum ScrollToAnimationEnum {
  AUTO = 'auto',
  SMOOTH = 'smooth',
}

interface ButtonConfig {
  className?: string;
  icon?: ReactNode;
  isActive?: boolean;
  isAnimate?: boolean;
  offset?: number;
  scrollTo?: number;
}

interface BackToProps {
  className?: string;
  elementToScrollRef?: MutableRefObject<HTMLElement | null>;
  bottom?: ButtonConfig | boolean;
  top?: ButtonConfig | boolean;
}
const BackTo: FC<BackToProps> = ({
  className,
  elementToScrollRef,
  bottom = {},
  top = {},
}) => {
  const bottomConfig = useMemo(
    () => ({
      icon: <ArrowDownward fontSize="inherit" />,
      isActive: (bottom as ButtonConfig)?.isActive || !!bottom,
      isAnimate: true,
      offset: DEFAULT_OFFSET,
      scrollTo: DEFAULT_SCROLL_TO,
      ...(bottom as ButtonConfig),
    }),
    [bottom],
  );

  const topConfig = useMemo(
    () => ({
      icon: <ArrowUpward fontSize="inherit" />,
      isActive: (top as ButtonConfig)?.isActive || !!top,
      isAnimate: true,
      offset: DEFAULT_OFFSET,
      scrollTo: DEFAULT_SCROLL_TO,
      ...(top as ButtonConfig),
    }),
    [top],
  );

  const innerWrapperRef = useRef(null);
  const ElementToScroll = elementToScrollRef?.current || window;
  const classes = useStyles({
    isWindowScroll: !elementToScrollRef?.current,
    wrapperHeight: innerWrapperRef?.current?.['clientHeight'] || 0,
  });

  const [isShown, setIsShown] = useState<Record<'bottom' | 'top', boolean>>({
    bottom: true,
    top: false,
  });

  const backToBottom = useCallback(() => {
    const targetOffsetTopPosition = isWindow(ElementToScroll)
      ? document.documentElement.scrollHeight -
        ElementToScroll.innerHeight -
        bottomConfig.scrollTo
      : ElementToScroll.scrollHeight -
        ElementToScroll.clientHeight -
        bottomConfig.scrollTo;

    ElementToScroll.scrollTo({
      left: 0,
      behavior: bottomConfig.isAnimate
        ? ScrollToAnimationEnum.SMOOTH
        : ScrollToAnimationEnum.AUTO,
      top: targetOffsetTopPosition,
    });
  }, [ElementToScroll, bottomConfig.isAnimate, bottomConfig.scrollTo]);

  const backToTop = useCallback(() => {
    const targetOffsetTopPosition = topConfig.scrollTo;

    ElementToScroll.scrollTo({
      left: 0,
      behavior: topConfig.isAnimate
        ? ScrollToAnimationEnum.SMOOTH
        : ScrollToAnimationEnum.AUTO,
      top: targetOffsetTopPosition,
    });
  }, [ElementToScroll, topConfig.isAnimate, topConfig.scrollTo]);

  const handleScroll = useCallback(() => {
    const currentOffsetTopPosition = isWindow(ElementToScroll)
      ? ElementToScroll.scrollY
      : ElementToScroll.scrollTop;

    const currentOffsetBottomPosition = isWindow(ElementToScroll)
      ? document.documentElement.scrollHeight -
        ElementToScroll.innerHeight -
        currentOffsetTopPosition
      : ElementToScroll.scrollHeight -
        ElementToScroll.clientHeight -
        currentOffsetTopPosition;

    const canBackToBottom =
      currentOffsetBottomPosition >= bottomConfig.offset &&
      currentOffsetBottomPosition > bottomConfig.scrollTo;
    const canBackToTop =
      currentOffsetTopPosition >= topConfig.offset &&
      currentOffsetTopPosition > topConfig.scrollTo;

    setIsShown({
      bottom: canBackToBottom,
      top: canBackToTop,
    });
  }, [
    ElementToScroll,
    bottomConfig.offset,
    bottomConfig.scrollTo,
    topConfig.offset,
    topConfig.scrollTo,
  ]);

  const debouncedHandleScroll = _.debounce(handleScroll, 200);

  useEffect(() => {
    if (ElementToScroll && (bottomConfig.isActive || topConfig.isActive)) {
      ElementToScroll?.addEventListener('scroll', debouncedHandleScroll);
    } else {
      ElementToScroll?.removeEventListener('scroll', debouncedHandleScroll);
    }

    return () => {
      ElementToScroll?.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [
    ElementToScroll,
    bottomConfig.isActive,
    debouncedHandleScroll,
    topConfig.isActive,
  ]);

  if (!topConfig.isActive && !bottomConfig.isActive) {
    return <Fragment />;
  }

  return (
    <div className={classes.wrapper}>
      <div
        className={clsx(classes.innerWrapper, className)}
        ref={innerWrapperRef}
      >
        {topConfig.isActive && (
          <IconButton
            data-testid="back-to-top"
            disableFocusRipple
            className={clsx(
              classes.button,
              { [classes.topIsShown]: isShown.top },
              topConfig.className,
            )}
            onClick={backToTop}
          >
            {topConfig.icon}
          </IconButton>
        )}

        {bottomConfig.isActive && (
          <IconButton
            data-testid="back-to-bottom"
            disableFocusRipple
            className={clsx(
              classes.button,
              { [classes.bottomIsShown]: isShown.bottom },
              bottomConfig.className,
            )}
            onClick={backToBottom}
          >
            {bottomConfig.icon}
          </IconButton>
        )}
      </div>
    </div>
  );
};

export { BackTo };
